/**
 * XIRR — Extended Internal Rate of Return
 * Computes the annualized rate of return for a series of irregular cash flows.
 *
 * Uses the Newton-Raphson root-finding method on XNPV.
 *
 * @param {Array<{date: Date|string, amount: number}>} cashflows
 *   Negative amounts = money paid out (investments).
 *   Positive amounts = money received (redemptions / current market value).
 * @returns {number|null} Annualized rate, e.g. 0.18 = 18% p.a., or null if not solvable.
 */
export function computeXIRR(cashflows) {
  if (!cashflows || cashflows.length < 2) return null

  // Parse + sort by date ascending
  const parsed = cashflows
    .map(cf => ({
      date: cf.date instanceof Date ? cf.date : new Date(cf.date),
      amount: cf.amount,
    }))
    .sort((a, b) => a.date - b.date)

  // Need at least one in-flow and one out-flow
  if (!parsed.some(cf => cf.amount < 0) || !parsed.some(cf => cf.amount > 0)) return null

  const t0 = parsed[0].date.getTime()
  const MS_PER_YEAR = 365.25 * 24 * 60 * 60 * 1000

  // XNPV: net present value at given rate
  const xnpv = (rate) =>
    parsed.reduce((sum, cf) => {
      const years = (cf.date.getTime() - t0) / MS_PER_YEAR
      return sum + cf.amount / Math.pow(1 + rate, years)
    }, 0)

  // Derivative of XNPV with respect to rate
  const xnpvDeriv = (rate) =>
    parsed.reduce((sum, cf) => {
      const years = (cf.date.getTime() - t0) / MS_PER_YEAR
      return sum - years * cf.amount / Math.pow(1 + rate, years + 1)
    }, 0)

  // Newton-Raphson
  let rate = 0.1
  for (let i = 0; i < 100; i++) {
    const f  = xnpv(rate)
    const df = xnpvDeriv(rate)
    if (Math.abs(df) < 1e-12) return null
    const next = rate - f / df
    if (!isFinite(next) || Math.abs(next) > 1e4) return null
    if (Math.abs(next - rate) < 1e-7) return isFinite(next) ? next : null
    rate = next
  }
  return null // didn't converge
}

/**
 * Format XIRR as a percentage string, e.g. "+18.5%"
 */
export function fmtXIRR(rate) {
  if (rate == null || !isFinite(rate)) return '—'
  const pct = rate * 100
  return (pct >= 0 ? '+' : '') + pct.toFixed(1) + '%'
}
